import { consumer, core } from '@tock/coretypes';
import { stringify } from '@tock/query-string';
import AppConstants from 'constants/AppConstants';
import consumerProto from 'utils/consumerProto';
import protos from 'utils/protos';

const { PatronQuizResponse, WishlistRequest, AddPlaylistItemRequest, Playlist } = consumerProto;
const { ValidatePhoneRequest, PatronImpressionRequest, PatronImpressionType } = protos;

/**
 * Actions relating to guest's viewing and managing their bookings.
 */

/**
 * Get a patron's history
 * @returns {Object} A redux TockApi action.
 */

export function fetchPurchaseHistory(): ApiAction {
  return {
    TockApi: {
      getListProto: '/purchase?count=1000',
      type: AppConstants.PATRON_FETCH_PURCHASE_HISTORY,
    },
  };
}

export function fetchPurchaseSummaries(): ApiAction {
  return {
    TockApi: {
      getListProto: '/purchase-summary?count=1000',
      type: AppConstants.PATRON_FETCH_PURCHASE_SUMMARIES,
    },
  };
}

export function fetchPurchasesAroundTime(dateTime: string, dinerEmail?: string) {
  const queryParam = stringify({
    dateTime: dateTime,
    radiusHours: '1',
    dinerEmail: dinerEmail,
  });
  return {
    TockApi: {
      getListProto: `/purchase-summary/time-range?${queryParam}`,
      context: {
        dateTime,
      },
      type: AppConstants.PATRON_FETCH_PURCHASES_AROUND_TIME,
    },
  };
}

/**
 * Get a patron's legacy transfer history
 * @returns {Object} A redux TockApi action.
 */
export function fetchLegacyTransferHistory(): ApiAction {
  return {
    TockApi: {
      getListProto: '/transfer/legacy',
      type: AppConstants.PATRON_FETCH_LEGACY_TRANSFERS,
    },
  };
}

export function fetchPartnerPurchaseHistory(partnerId: number): ApiAction {
  return {
    TockApi: {
      getListProto: `/partner/reservations/all?partnerId=${partnerId}`,
      type: AppConstants.PARTNER_FETCH_ALL_RESERVATIONS,
    },
  };
}

export function fetchAchievements(): ApiAction {
  return {
    TockApi: {
      getListProto: '/patron/achievements',
      type: AppConstants.PATRON_FETCH_ACHIEVEMENTS,
    },
  };
}

export function fetchAllAchievements(): ApiAction {
  return {
    TockApi: {
      getListProto: '/achievements',
      type: AppConstants.FETCH_ALL_ACHIEVEMENTS,
    },
  };
}

export function fetchQuizQuestions(): ApiAction {
  return {
    TockApi: {
      getListProto: '/patron-quiz',
      type: AppConstants.PATRON_FETCH_QUIZ_QUESTIONS,
    },
  };
}

export function fetchQuizResults(): ApiAction {
  return {
    TockApi: {
      getListProto: '/patron/profile/quiz/results',
      type: AppConstants.PATRON_FETCH_QUIZ_RESULTS,
    },
  };
}

export function fetchQuizAnswers(): ApiAction {
  return {
    TockApi: {
      getListProto: '/patron/profile/quiz/answers',
      type: AppConstants.PATRON_FETCH_QUIZ_ANSWERS,
    },
  };
}

export function submitQuizResponses(
  responseIds: number[],
  toastArg?: { hideToast: boolean }
): ApiAction {
  return {
    TockApi: {
      putProto: '/patron/profile/quiz',
      type: AppConstants.PATRON_PUT_QUIZ_RESPONSES,
      request: PatronQuizResponse.fromObject({
        selectedOption: responseIds,
      }),
      toast: toastArg?.hideToast ? undefined : 'Quiz submitted',
    },
  };
}

export function fetchAllPreferenceOptions(): ApiAction {
  return {
    TockApi: {
      getProto: '/preferences',
      type: AppConstants.ALL_PREFERENCE_OPTIONS_FETCH,
    },
  };
}

export function openEditPatronProfileModal(): BaseTockAction {
  return {
    type: AppConstants.PATRON_OPEN_EDIT_PROFILE,
  };
}

export function closeEditPatronProfileModal(): BaseTockAction {
  return {
    type: AppConstants.PATRON_CLOSE_EDIT_PROFILE,
  };
}

export function openQuizModal(): BaseTockAction {
  return {
    type: AppConstants.PATRON_OPEN_QUIZ_MODAL,
  };
}

export function closeQuizModal(): BaseTockAction {
  return {
    type: AppConstants.PATRON_CLOSE_QUIZ_MODAL,
  };
}

export function addToWishlist(requestData: consumer.IWishlistRequest): ApiAction {
  return {
    TockApi: {
      postProto: '/time/wishlist',
      type: AppConstants.PATRON_ADD_TO_WISHLIST,
      request: WishlistRequest.fromObject(requestData),
    },
  };
}

export function fetchWishlist(): ApiAction {
  return {
    TockApi: {
      getListProto: '/time/wishlist',
      type: AppConstants.PATRON_FETCH_WISHLIST,
    },
  };
}

export function validatePhoneRequest(
  request: core.ValidatePhoneRequest
): ApiAction<core.ValidatePhoneResponse> {
  return {
    TockApi: {
      request: ValidatePhoneRequest.fromObject(request),
      postProto: '/consumer/phone/validate',
      type: AppConstants.VALIDATE_PHONE_REQUEST,
    },
  };
}

export function fetchPlaylists(patronId: number): ApiAction<consumer.Playlist[]> {
  return {
    TockApi: {
      getListProto: `/patron/${patronId}/profile/playlists`,
      type: AppConstants.PATRON_PLAYLISTS_FETCH,
    },
  };
}

export function upsertPlaylistItem(
  playlistId: number,
  addRequest: consumer.IAddPlaylistItemRequest
): ApiAction<consumer.Playlist> {
  return {
    TockApi: {
      postProto: `/patron/profile/playlist/${playlistId}/item`,
      request: AddPlaylistItemRequest.fromObject(addRequest),
      type: AppConstants.PATRON_PLAYLIST_ADD_ITEM,
    },
  };
}

export function upsertPlaylist(playlist: consumer.IPlaylist): ApiAction<consumer.Playlist> {
  return {
    TockApi: {
      postProto: '/patron/profile/playlist',
      request: Playlist.fromObject(playlist),
      type: AppConstants.PATRON_PLAYLIST_UPSERT,
    },
  };
}

export function deletePlaylistItem(playlistId: number, playlistItemId: number): ApiAction<number> {
  return {
    TockApi: {
      deleteProto: `/patron/profile/playlist/${playlistId}/item/${playlistItemId}`,
      type: AppConstants.PATRON_PLAYLIST_ITEM_DELETE,
      context: {
        playlistId,
        playlistItemId,
      },
    },
  };
}

export function patronB2cMarketingOptInImpression(
  patronId: number,
  prompt: string
): ApiAction<core.PatronImpression> {
  return {
    TockApi: {
      postProto: '/patron/impression',
      request: PatronImpressionRequest.fromObject({
        patronId,
        type: PatronImpressionType.B2C_MARKETING_OPT_IN,
        buildNumber: window.__BUILD_NUMBER__ || 'dev',
        data: 'b2cMarketingOptInData',
        b2cMarketingOptInData: {
          prompt,
        },
      }),
      type: AppConstants.PATRON_IMPRESSION,
    },
  };
}
